import React from 'react';
import { Layout } from 'antd';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { DashboardOutlined, ReadOutlined, FormOutlined, UserOutlined, SlidersOutlined, FolderOutlined, BankOutlined, ImportOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { map } from 'underscore';

import './sider.scss';
import logoSrc from '../../../assets/images/logo-full-white.webp';
import { SiderProps } from './types';
import { CombinedStates } from '../../../core/types';

type MenuItem = {
    key: string;
    label: string;
    link: string;
    icon: React.ReactNode;
    className?: string;
    style?: React.CSSProperties | null;
    isActive?: boolean;
    roles?: number[];
    children?: MenuItem[];
};

const menuItems: MenuItem[] = [
    {
        key: '1',
        label: 'Darbalaukis',
        link: '/dashboard',
        icon: <DashboardOutlined />,
    },
    {
        key: '1',
        label: 'Duomenų importavimas',
        link: '/duomenu-importavimas',
        roles: [1, 2, 3, 4],
        icon: <ImportOutlined />,
    },
    {
        key: '2',
        label: 'Mokyklų administravimas',
        link: '/mokyklos',
        roles: [1, 2],
        icon: <ReadOutlined />,
    },
    {
        key: '3',
        label: 'Apklausos',
        link: '/apklausos',
        icon: <FormOutlined />,
    },
    {
        key: '4',
        label: 'Vartotojai',
        link: '/vartotojai',
        roles: [1, 2],
        icon: <UserOutlined />,
    },
    {
        key: '5',
        label: 'Švietimo centro rodikliai',
        link: '/svietimo-centro-rodikliai',
        icon: <BankOutlined />,
        roles: [1, 2, 4],
    },
    {
        key: '6',
        label: 'Švietimo skyriaus rodikliai',
        link: '/svietimo-skyriaus-rodikliai',
        icon: <BankOutlined />,
        roles: [1, 2, 3],
    },
    {
        key: '7',
        label: 'Egzaminai',
        link: '/egzaminai',
        icon: <ReadOutlined />,
        roles: [1, 2, 3, 4],
    },
    {
        key: '8',
        label: 'Rodiklių administravimas',
        link: '/rodikliai',
        icon: <SlidersOutlined />,
        roles: [1, 2],
        children: [
            {
                key: '8-1',
                label: 'Rodiklių administravimas',
                link: '/rodikliai',
                icon: <SlidersOutlined />,
            },
            {
                key: '8-2',
                label: 'Rodiklių kategorijos',
                link: '/rodikliu-kategorijos',
                icon: <FolderOutlined />,
            },
        ],
    },
    {
        key: '9',
        label: 'Pagalba',
        link: '/pagalba',
        icon: <QuestionCircleOutlined />,
        roles: [1, 2, 3, 4, 5],
    },
];

const Sider = ({
    children,
    floating,
    beforeSider,
    afterSider,
    displayAsDiv,
}:SiderProps) => {

    const navigate = useNavigate();
    const location = useLocation();
    const {
        userData,
    } = useSelector((state:CombinedStates) => state.user);

    const handleNavigation = (link: string) => {
        navigate(link);
    };

    const handleKeyPress = (event: React.KeyboardEvent, link: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleNavigation(link);
        }
    };

    const Wrapper: React.ElementType = displayAsDiv ? 'div' : Layout.Sider;
    const wrapperProps = displayAsDiv
        ? { className: 'responsive-sider' }
        : { width: '260px', className: 'main-sider' };

    return (
        <Wrapper {...wrapperProps}>
            {beforeSider}
            <div className="sider-content">
                <div className="sider-logo-wrapper">
                    <img
                        src={logoSrc}
                        alt="VKVIS Logo"
                        className="sider-logo"
                        loading="eager" />
                </div>
                <div className="sider-content-wrapper">
                    <nav className="menu__content" role="navigation" aria-label="Main navigation">
                        <ul className="menu__nav">
                            {menuItems
                                .filter(item => !item.roles || item.roles.includes(userData?.role_id || 0))
                                .map((item) => {
                                    const isActive = location.pathname === item.link
                                        || item.children?.some(child => location.pathname.startsWith(child.link));

                                    const visibleChildren = (item.children || []).filter(child => !child.roles || child.roles.includes(userData?.role_id || 0));

                                    return (
                                        <li key={item.key}>
                                            <button
                                                className={isActive ? 'active' : ''}
                                                onClick={() => handleNavigation(item.link)}
                                                onKeyDown={(e) => handleKeyPress(e, item.link)}
                                                type="button"
                                                aria-expanded={visibleChildren.length > 0 ? isActive : undefined}
                                                aria-haspopup={visibleChildren.length > 0 ? 'true' : undefined}
                                                aria-label={`Navigate to ${item.label}`}
                                                tabIndex={0}>
                                                <span className="menu__nav-icon" aria-hidden="true">
                                                    {item.icon}
                                                </span>
                                                <span>{item.label}</span>
                                            </button>

                                            {visibleChildren.length > 0 && isActive && (
                                                <ul className="menu__subnav" role="menu">
                                                    {visibleChildren.map((child) => {
                                                        const isChildActive = location.pathname === child.link;
                                                        return (
                                                            <li key={child.key} role="none">
                                                                <button
                                                                    className={isChildActive ? 'active' : ''}
                                                                    onClick={() => handleNavigation(child.link)}
                                                                    onKeyDown={(e) => handleKeyPress(e, child.link)}
                                                                    type="button"
                                                                    role="menuitem"
                                                                    aria-label={`Navigate to ${child.label}`}
                                                                    tabIndex={0}>
                                                                    {child.icon && (
                                                                        <span className="menu__nav-icon" aria-hidden="true">
                                                                            {child.icon}
                                                                        </span>
                                                                    )}
                                                                    <span>{child.label}</span>
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>
                    </nav>
                    {children && (
                        <div className="sider-children">
                            {children}
                        </div>
                    )}
                </div>
            </div>
            {afterSider}
        </Wrapper>
    );
};

Sider.defaultProps = {
    children: null,
    floating: false,
    beforeFooter: '',
    afterFooter: '',
};

export default Sider;
